import React, { useState } from 'react';
import { healthCheck, generateSQL } from './api';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Bug, Activity, Zap } from 'lucide-react';

const DebugPanel: React.FC = () => {
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [testing, setTesting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const testHealthCheck = async () => {
    setTesting(true);
    setDebugInfo('Testing health check...\n');
    
    try {
      const result = await healthCheck();
      setDebugInfo(prev => prev + `✅ Health check successful:\n${JSON.stringify(result, null, 2)}\n`);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setDebugInfo(prev => prev + `❌ Health check failed:\n${errorMsg}\n`);
    } finally {
      setTesting(false);
    }
  };

  const testSQLGeneration = async () => {
    setTesting(true);
    setDebugInfo('Testing SQL generation with sample data...\n');
    
    try {
      // Create a simple test CSV file
      const csvContent = 'table_name,column_name,data_type\nusers,id,INTEGER\nusers,name,VARCHAR(255)\nusers,email,VARCHAR(255)';
      const testFile = new File([csvContent], 'test-schema.csv', { type: 'text/csv' });
      
      setDebugInfo(prev => prev + '📤 Sending request to API...\n');
      const result = await generateSQL(testFile, 'Get all users', 'postgres', '10');
      
      setDebugInfo(prev => prev + `✅ SQL generation successful:\n${JSON.stringify(result, null, 2)}\n`);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setDebugInfo(prev => prev + `❌ SQL generation failed:\n${errorMsg}\n`);
      
      // Also log any additional error details from console
      setDebugInfo(prev => prev + `\n🔍 Check browser console for detailed API response logs\n`);
    } finally {
      setTesting(false);
    }
  };

  const testEnvironment = () => {
    const info = {
      hostname: window.location.hostname,
      href: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
    
    setDebugInfo(`🔍 Environment Info:\n${JSON.stringify(info, null, 2)}\n`);
  };

  return (
    <div className="mb-6">
      <Card className="border-dashed border-gray-300 dark:border-gray-600 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Bug className="h-4 w-4" />
              Debug Panel
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(!isVisible)}
              className="h-auto p-1 text-muted-foreground hover:text-foreground"
            >
              {isVisible ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        
        {isVisible && (
          <CardContent className="pt-0">
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <Button 
                onClick={testEnvironment}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial h-8 text-xs"
              >
                <Activity className="h-3 w-3 mr-1" />
                Environment
              </Button>
              <Button 
                onClick={testHealthCheck}
                disabled={testing}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial h-8 text-xs"
              >
                <Activity className="h-3 w-3 mr-1" />
                {testing ? 'Testing...' : 'API Health'}
              </Button>
              <Button 
                onClick={testSQLGeneration}
                disabled={testing}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial h-8 text-xs"
              >
                <Zap className="h-3 w-3 mr-1" />
                {testing ? 'Testing...' : 'SQL Test'}
              </Button>
            </div>
            
            <div className="text-xs text-muted-foreground mb-3 p-2 bg-muted/50 rounded border">
              💡 SQL test shows API responses in browser console. Check DevTools → Console for details.
            </div>
            
            {debugInfo && (
              <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-80 whitespace-pre-wrap font-mono border">
                {debugInfo}
              </pre>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default DebugPanel;